import { c as _c } from "react/compiler-runtime";
import cx from 'classnames';
import { isFinite } from 'lodash';
import React, { use } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Col, ColProps, CostDisplay, pickDollarCurrencyDisplay, Row, RowProps, Skeleton, Typography } from '@commutifi-fe/ui';
import { convertToUnit, formatToTimeString, getLocaleMeasurementUnits } from '@commutifi-fe/utils';
import { DEFAULT_UNITS_BY_MEASURE_TYPE, Factors, LocalesType, MeasureTypes, screenSizes } from '@commutifi-fe/constants';
import { PlanCostType, PlanFrequency } from '@commutifi/models/Plan';
import { CurrencyCode } from '@commutifi/constants/Locale';
import { DEFAULT_LOCALE, FormatNumberOptions, useComponentIntl } from '../../locales';
import { MetricsCardContextValue, MetricsContext } from './MetricsContext';
import { FrequencyDisplay } from '../FrequencyDisplay';
import { MetricLabel } from './MetricLabel';
import styles from './styles.module.scss';
const {
  Text
} = Typography;
interface MetricsProps {
  locale: LocalesType | undefined;
  isLoading?: boolean;
  children: React.ReactNode;
  rowProps?: RowProps;
  colProps?: ColProps;
}
type CostProps = NumberMetric & FormatNumberOptions & {
  // Either fixed cost, variable or free.
  // There is some business logic based on this cost type
  costType?: PlanCostType;
  children?: React.ReactNode;
};
interface MetricProps<T> {
  value?: T | null;
  className?: string;
}
export type NumberMetric = MetricProps<number>;
function DescriptionItemLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ItemLabel className="u-margin-top--small"><Skeleton.Input style={{
        minWidth: 75,
        width: 75
      }} active={true} size="small" /></ItemLabel>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function MetricsLocalizer(t0) {
  const $ = _c(9);
  const {
    isLoading: t1,
    children,
    locale: l
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const locale = l || DEFAULT_LOCALE;
  const currency = CurrencyCode.USD;
  let t2;
  if ($[0] !== locale) {
    t2 = getLocaleMeasurementUnits(locale);
    $[0] = locale;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const localeMeasurementUnits = t2;
  let t3;
  if ($[2] !== isLoading || $[3] !== locale || $[4] !== localeMeasurementUnits) {
    t3 = {
      isLoading,
      locale,
      currency,
      localeMeasurementUnits
    };
    $[2] = isLoading;
    $[3] = locale;
    $[4] = localeMeasurementUnits;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const value = t3;
  let t4;
  if ($[6] !== children || $[7] !== value) {
    t4 = <MetricsContext value={value}>{children}</MetricsContext>;
    $[6] = children;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function MetricsCard(t0) {
  const $ = _c(22);
  const {
    isLoading: t1,
    children,
    rowProps,
    colProps,
    locale: l
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const locale = l || DEFAULT_LOCALE;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: screenSizes.smMaxWidth
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const isSmallAndDown = useMediaQuery(t2);
  const currency = CurrencyCode.USD;
  let t3;
  if ($[1] !== locale) {
    t3 = getLocaleMeasurementUnits(locale);
    $[1] = locale;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const localeMeasurementUnits = t3;
  let t4;
  if ($[3] !== isLoading || $[4] !== locale || $[5] !== localeMeasurementUnits) {
    t4 = {
      isLoading,
      locale,
      currency,
      localeMeasurementUnits
    };
    $[3] = isLoading;
    $[4] = locale;
    $[5] = localeMeasurementUnits;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const value = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [20, 20];
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isLoading ? "metrics-loading" : "metrics";
  let t7;
  if ($[8] !== children || $[9] !== colProps || $[10] !== isSmallAndDown) {
    let t8;
    if ($[12] !== colProps || $[13] !== isSmallAndDown) {
      t8 = child => <Col xs={isSmallAndDown ? 12 : 6} {...colProps}>{child}</Col>;
      $[12] = colProps;
      $[13] = isSmallAndDown;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = React.Children.map(children, t8);
    $[8] = children;
    $[9] = colProps;
    $[10] = isSmallAndDown;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[15] !== rowProps || $[16] !== t6 || $[17] !== t7) {
    t8 = <Row gutter={t5} {...rowProps} data-testid={t6}>{t7}</Row>;
    $[15] = rowProps;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8 || $[20] !== value) {
    t9 = <MetricsContext value={value}>{t8}</MetricsContext>;
    $[19] = t8;
    $[20] = value;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
const useMetricsCardContext = (opts?: {
  shouldThrow: boolean;
}) => {
  const context = use(MetricsContext);
  if (!context) {
    if (opts?.shouldThrow === false) {
      return {} as MetricsCardContextValue;
    }
    throw new Error('components cannot be rendered outside Metrics or MetricsCard component');
  }
  return context;
};
function ItemLabel(t0) {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("u-font-24 u-font-weight-semi u-color--neutral-600", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Score(t0) {
  const $ = _c(10);
  const {
    value,
    className
  } = t0;
  const intl = useComponentIntl("Metrics");
  const {
    isLoading
  } = useMetricsCardContext();
  let t1;
  if ($[0] !== className) {
    t1 = cx(styles["metrics-item-score"], className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Text strong="semi-bold" type="secondary"><MetricLabel metricType={Factors.Score} /></Text>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== intl || $[4] !== isLoading || $[5] !== value) {
    t3 = isLoading ? <DescriptionItemLoading /> : <ItemLabel>{isFinite(value) ? intl.formatNumber(Math.round(value)) : "\u2014"}</ItemLabel>;
    $[3] = intl;
    $[4] = isLoading;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function Carbon(t0) {
  const $ = _c(3);
  const {
    value,
    className,
    ...formatOptions
  } = t0;
  const {
    localeMeasurementUnits
  } = useMetricsCardContext();
  const intl = useComponentIntl("Metrics");
  const carbonUnit = localeMeasurementUnits[MeasureTypes.Mass];
  const defaultUnit = DEFAULT_UNITS_BY_MEASURE_TYPE[MeasureTypes.Mass];
  let t1;
  bb0: {
    if (typeof value === "string" && Number.isNaN(parseFloat(value))) {
      t1 = value;
      break bb0;
    }
    if (value && defaultUnit !== carbonUnit) {
      const t2 = `${value} ${defaultUnit}`;
      let t3;
      if ($[0] !== carbonUnit || $[1] !== t2) {
        t3 = convertToUnit(t2, carbonUnit);
        $[0] = carbonUnit;
        $[1] = t2;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t1 = t3;
      break bb0;
    }
    t1 = value;
  }
  const localeCarbon = t1;
  return isFinite(value) ? <>{intl.formatNumber(parseFloat((localeCarbon as number).toFixed(2)), {
      maximumFractionDigits: 0,
      ...formatOptions
    })}{" "}{carbonUnit}</> : <span className={className}>—</span>;
}
function CarbonItem(t0) {
  const $ = _c(22);
  let children;
  let className;
  let formatOptions;
  let frequency;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      className,
      frequency,
      children,
      ...formatOptions
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = formatOptions;
    $[4] = frequency;
    $[5] = value;
  } else {
    children = $[1];
    className = $[2];
    formatOptions = $[3];
    frequency = $[4];
    value = $[5];
  }
  const {
    isLoading
  } = useMetricsCardContext();
  let t1;
  if ($[6] !== className) {
    t1 = cx(styles["metrics-item-carbon"], className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MetricLabel metricType={Factors.Carbon} />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== frequency) {
    t3 = frequency ? <FrequencyDisplay frequency={frequency} /> : null;
    $[9] = frequency;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <Text strong="semi-bold" type="secondary">{t2} {t3}</Text>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== formatOptions || $[14] !== isLoading || $[15] !== value) {
    t5 = isLoading ? <DescriptionItemLoading /> : <ItemLabel><Carbon value={value} {...formatOptions} /></ItemLabel>;
    $[13] = formatOptions;
    $[14] = isLoading;
    $[15] = value;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== children || $[18] !== t1 || $[19] !== t4 || $[20] !== t5) {
    t6 = <div className={t1}>{t4}{t5}{children}</div>;
    $[17] = children;
    $[18] = t1;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}
function Time(t0) {
  const $ = _c(5);
  const {
    value,
    className
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = isFinite(value) ? formatToTimeString(value, value >= 3600 ? "h[_] m" : "h[_] m[_]") : "\u2014";
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== t1) {
    t2 = <span className={className}>{t1}</span>;
    $[2] = className;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function TimeItem(t0) {
  const $ = _c(15);
  const {
    value,
    className,
    frequency,
    children
  } = t0;
  const {
    isLoading
  } = useMetricsCardContext();
  let t1;
  if ($[0] !== className) {
    t1 = cx(styles["metrics-item-time"], className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MetricLabel metricType={Factors.Time} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== frequency) {
    t3 = frequency ? <FrequencyDisplay frequency={frequency} /> : null;
    $[3] = frequency;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <Text strong="semi-bold" type="secondary">{t2} {t3}</Text>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoading || $[8] !== value) {
    t5 = isLoading ? <DescriptionItemLoading /> : <ItemLabel><Time value={value} /></ItemLabel>;
    $[7] = isLoading;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t1}>{t4}{t5}{children}</div>;
    $[10] = children;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/**
 * Display a localized cost properly formatted
 * @param props - See interface definition {@link CostProps}
 *
 * @returns JSX Element representing the formatted subsidized cost or cost:
 *            1. If you pass a param 'subsidy' and we can apply that to the cost : display the subsidized cost
 *            2. In all other cases, if the cost is defined or as a type we should return a text we
 *               display it and fallback to a dash
 */
function Cost(t0) {
  const $ = _c(18);
  let children;
  let costType;
  let currency;
  let options;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      costType,
      currency,
      children,
      ...options
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = costType;
    $[3] = currency;
    $[4] = options;
    $[5] = value;
  } else {
    children = $[1];
    costType = $[2];
    currency = $[3];
    options = $[4];
    value = $[5];
  }
  const t1 = !currency;
  let t2;
  if ($[6] !== t1) {
    t2 = {
      shouldThrow: t1
    };
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    currency: contextCurrency,
    locale
  } = useMetricsCardContext(t2);
  const currencyProp = currency || contextCurrency;
  let t3;
  if ($[8] !== currencyProp || $[9] !== locale) {
    t3 = currencyProp && locale ? pickDollarCurrencyDisplay(locale, currencyProp) : "symbol";
    $[8] = currencyProp;
    $[9] = locale;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const currencyDisplay = t3;
  let t4;
  if ($[11] !== children || $[12] !== costType || $[13] !== currencyDisplay || $[14] !== currencyProp || $[15] !== options || $[16] !== value) {
    t4 = <CostDisplay value={value} costType={costType} currency={currencyProp} currencyDisplay={currencyDisplay} {...options}>{children}</CostDisplay>;
    $[11] = children;
    $[12] = costType;
    $[13] = currencyDisplay;
    $[14] = currencyProp;
    $[15] = options;
    $[16] = value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}

/**
 * Very similar to Cost component but actually display the formatted cost
 * over a colored background with a label 'Cost' over the formatted value.
 *
 * @param props - See interface definition {@link CostProps}
 */
function CostItem(t0) {
  const $ = _c(20);
  let children;
  let className;
  let frequency;
  let props;
  if ($[0] !== t0) {
    ({
      frequency,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = frequency;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    frequency = $[3];
    props = $[4];
  }
  const {
    isLoading
  } = useMetricsCardContext();
  let t1;
  if ($[5] !== className) {
    t1 = cx(styles["metrics-item-cost"], className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MetricLabel metricType={Factors.Cost} />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== frequency) {
    t3 = frequency ? <FrequencyDisplay frequency={frequency} /> : null;
    $[8] = frequency;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <Text strong="semi-bold" type="secondary">{t2} {t3}</Text>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isLoading || $[13] !== props) {
    t5 = isLoading ? <DescriptionItemLoading /> : <ItemLabel><Cost {...props} /></ItemLabel>;
    $[12] = isLoading;
    $[13] = props;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== t1 || $[17] !== t4 || $[18] !== t5) {
    t6 = <div className={t1}>{t4}{t5}{children}</div>;
    $[15] = children;
    $[16] = t1;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
type DistanceProps = NumberMetric & FormatNumberOptions;
function Distance(t0) {
  const $ = _c(3);
  const {
    value,
    ...formatNumberProps
  } = t0;
  const {
    localeMeasurementUnits
  } = useMetricsCardContext();
  const intl = useComponentIntl("Metrics");
  const distanceUnit = localeMeasurementUnits[MeasureTypes.Distance];
  const defaultUnit = DEFAULT_UNITS_BY_MEASURE_TYPE[MeasureTypes.Distance];
  let t1;
  bb0: {
    if (typeof value === "string" && Number.isNaN(parseInt(value))) {
      t1 = value;
      break bb0;
    }
    if (value && defaultUnit !== distanceUnit) {
      const t2 = `${value} ${defaultUnit}`;
      let t3;
      if ($[0] !== distanceUnit || $[1] !== t2) {
        t3 = convertToUnit(t2, distanceUnit);
        $[0] = distanceUnit;
        $[1] = t2;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t1 = t3;
      break bb0;
    }
    t1 = value;
  }
  const localeDistance = t1;
  return isFinite(localeDistance) ? <>{intl.formatNumber(localeDistance, {
      maximumFractionDigits: localeDistance && localeDistance > 100 ? 0 : 2,
      ...formatNumberProps
    })}{" "}{distanceUnit}</> : "\u2014";
}
MetricsCard.Score = Score;
MetricsCard.Carbon = CarbonItem;
MetricsCard.Time = TimeItem;
MetricsCard.Cost = CostItem;
MetricsLocalizer.Distance = Distance;
MetricsLocalizer.Carbon = Carbon;
MetricsLocalizer.Time = Time;
MetricsLocalizer.Cost = Cost;
export default MetricsLocalizer;